"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Multisig = void 0;
const network_1 = require("../network");
const cosmos_1 = require("../utils/cosmos");
class Multisig {
    constructor() {
        this.init = (provider, multisig) => __awaiter(this, void 0, void 0, function* () {
            const chainId = (yield provider.getChainId());
            if (!network_1.SUPPORTED_CHAINS.includes(chainId))
                throw new Error('Chain not supported');
            const config = (0, network_1.getNetworkConfig)(chainId);
            this.provider = provider;
            this.config = config;
            this.multisig = multisig;
        });
        this.createProposal = (args) => __awaiter(this, void 0, void 0, function* () {
            const { messages, description, manualExecution, from } = args;
            const proposeMsg = {
                propose_transaction: {
                    msgs: messages,
                    description,
                    manual_execution: manualExecution,
                },
            };
            const { events, transactionHash } = yield this._sendMessage({ from, message: proposeMsg });
            const proposal = (0, cosmos_1.getEventAttribute)(events, 'wasm-propose_transaction', 'tx_id');
            return {
                proposal,
                transactionHash,
                events,
            };
        });
        this.voteProposal = (args) => __awaiter(this, void 0, void 0, function* () {
            const { proposal, vote, from } = args;
            const multisig = this.multisig;
            const voteMessage = {
                vote_transaction: {
                    tx_id: proposal,
                    vote,
                },
            };
            const { events, transactionHash } = yield this._sendMessage({ from, message: voteMessage });
            const { status } = (yield this._makeQuery({ get_transaction_status: proposal }));
            return {
                status,
                transactionHash,
                events,
            };
        });
        this.executeProposal = (args) => __awaiter(this, void 0, void 0, function* () {
            const { proposal, from } = args;
            const executeMsg = {
                execute_transaction: proposal,
            };
            const { events, transactionHash } = yield this._sendMessage({ from, message: executeMsg });
            const status = (0, cosmos_1.getEventAttribute)(events, 'wasm-update_proposal_status', 'new_status');
            return { status, transactionHash, events };
        });
        this.proposeIBCTransfer = (args) => __awaiter(this, void 0, void 0, function* () {
            const { from, channel, to, amount, description, manualExecution, timestamp } = args;
            const response = yield this.createProposal({
                from,
                description: description || `IBC Transfer to ${to}. Amount ${amount}`,
                manualExecution,
                messages: [
                    {
                        ibc: {
                            transfer: {
                                channel_id: channel,
                                to_address: to,
                                amount,
                                timeout: {
                                    timestamp,
                                },
                            },
                        },
                    },
                ],
            });
            return response;
        });
        this.proposeAddMembers = (args) => __awaiter(this, void 0, void 0, function* () {
            const { new_members, from, description, manualExecution, newApproval } = args;
            const { members: current, approval } = yield this.getConfiguration();
            const lowercased = new_members.map((memb) => memb.toLowerCase());
            for (const memb of current) {
                if (lowercased.includes(memb.toLowerCase()))
                    throw new Error(`Member ${memb} already exists`);
            }
            const final = current.concat(new_members);
            return this.proposeNewConfiguration({
                members: final,
                approval: newApproval || approval,
                from,
                description: description || `Add members: ${current.join(',')}`,
                manualExecution,
            });
        });
        this.proposeRemoveMembers = (args) => __awaiter(this, void 0, void 0, function* () {
            const { removed, from, description, manualExecution, newApproval } = args;
            const { members: current, approval } = yield this.getConfiguration();
            const lowercased = removed.map((memb) => memb.toLowerCase());
            for (const memb of lowercased)
                if (!current.includes(memb.toLowerCase()))
                    throw new Error(`Member ${memb} is not part of multisig`);
            const final = current.filter((cur) => !removed.includes(cur.toLowerCase()));
            return this.proposeNewConfiguration({
                members: final,
                approval: newApproval || approval,
                from,
                description: description || `Remove members: ${current.join(',')}`,
                manualExecution,
            });
        });
        this.proposeMinimumApproval = (args) => __awaiter(this, void 0, void 0, function* () {
            const { newApproval, from, description, manualExecution } = args;
            const { members: current, approval } = yield this.getConfiguration();
            return this.proposeNewConfiguration({
                members: current,
                approval: newApproval,
                from,
                description: description || `Update approval from ${approval} to ${newApproval}`,
                manualExecution,
            });
        });
        this.getConfiguration = () => __awaiter(this, void 0, void 0, function* () {
            const { approval, members } = yield (0, cosmos_1.simultaneousAsync)({
                approval: () => this.provider.queryContractSmart(this.multisig, { get_min_approval: [] }),
                members: () => this.provider.queryContractSmart(this.multisig, { get_members: [] }),
            });
            return {
                approval: approval.count,
                members: members.members,
            };
        });
        this.proposeNewConfiguration = (args) => __awaiter(this, void 0, void 0, function* () {
            const { members, approval, from, description, manualExecution } = args;
            const changeConfigMsg = this._encodeMessage({
                configure_multisig: {
                    members,
                    min_approval: approval,
                },
            });
            this.validateConfiguration({ approval, members });
            return yield this.createProposal({
                messages: [
                    {
                        wasm: {
                            execute: {
                                contract_addr: this.multisig,
                                msg: changeConfigMsg,
                                funds: [],
                            },
                        },
                    },
                ],
                description,
                manualExecution,
                from,
            });
        });
        this.validateConfiguration = (args) => __awaiter(this, void 0, void 0, function* () {
            const { approval, members } = args;
            const nonRepeated = [...new Set(members)];
            if (approval <= 0)
                throw new Error('Approval cannot be zero');
            if (members.length >= approval)
                throw new Error('Approval cannot be bigger than members count');
            if (nonRepeated.length !== members.length)
                throw new Error('Cannot have repeated members');
        });
        this._makeQuery = (query) => __awaiter(this, void 0, void 0, function* () {
            return yield this.provider.queryContractSmart(this.multisig, query);
        });
        this._sendMessage = (args) => __awaiter(this, void 0, void 0, function* () {
            return yield this.provider.execute(args.from, this.multisig, args.message, 'auto');
        });
        this._encodeMessage = (message) => {
            return Buffer.from(JSON.stringify(message)).toString('base64');
        };
    }
}
exports.Multisig = Multisig;
//# sourceMappingURL=multisig.js.map